/**
 * TutorialTooltip - Floating tooltip with arrow pointing to target element.
 * Designed for older users with large text and buttons.
 */

import React, { useMemo, useRef, useLayoutEffect, useState } from 'react';
import './Tutorial.css';

function TutorialTooltip({
  step,
  targetRect,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip
}) {
  const tooltipRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(null);

  // Measure actual tooltip height after render
  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const height = tooltipRef.current.offsetHeight;
      if (height > 0 && height !== measuredHeight) {
        setMeasuredHeight(height);
      }
    }
  });

  // Calculate tooltip position based on target element
  const tooltipStyle = useMemo(() => {
    if (!step) return {};

    // Center screen for completion step or no target
    if (step.position === 'center' || !targetRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const rect = targetRect.originalRect || targetRect;
    const tooltipWidth = 320;
    const tooltipHeight = measuredHeight || 200;
    const margin = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top, left;
    let actualPosition = step.position;

    // Calculate position based on preference
    switch (step.position) {
      case 'bottom':
        top = targetRect.top + targetRect.height + margin;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'top':
        top = targetRect.top - tooltipHeight - margin;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        left = targetRect.left - tooltipWidth - margin;
        break;
      case 'right':
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        left = targetRect.left + targetRect.width + margin;
        break;
      default: // 'auto' - find best position
        // Prefer bottom, then top, then sides
        if (targetRect.top + targetRect.height + tooltipHeight + margin < viewportHeight) {
          actualPosition = 'bottom';
          top = targetRect.top + targetRect.height + margin;
          left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        } else if (targetRect.top - tooltipHeight - margin > 0) {
          actualPosition = 'top';
          top = targetRect.top - tooltipHeight - margin;
          left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        } else {
          actualPosition = 'right';
          top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
          left = targetRect.left + targetRect.width + margin;
        }
    }

    // Keep within viewport bounds
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin));
    top = Math.max(margin, Math.min(top, viewportHeight - tooltipHeight - margin));

    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      '--arrow-position': actualPosition
    };
  }, [step, targetRect, measuredHeight]);

  // Calculate arrow position
  const arrowPosition = useMemo(() => {
    if (!step || step.position === 'center' || !targetRect) return null;

    // Arrow points toward the target
    switch (step.position) {
      case 'bottom': return 'top';
      case 'top': return 'bottom';
      case 'left': return 'right';
      case 'right': return 'left';
      default: return 'top';
    }
  }, [step, targetRect]);

  if (!step) return null;

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;

  return (
    <div
      ref={tooltipRef}
      className={`tutorial-tooltip tutorial-tooltip--${step.position || 'auto'}`}
      style={tooltipStyle}
      role="dialog"
      aria-modal="true"
      aria-label="Tutorial step"
    >
      {/* Arrow pointing to target */}
      {targetRect && step.position !== 'center' && arrowPosition && (
        <div className={`tutorial-tooltip-arrow tutorial-tooltip-arrow--${arrowPosition}`} />
      )}

      {/* Content */}
      <div className="tutorial-tooltip-content">
        <h3 className="tutorial-tooltip-title">{step.title}</h3>
        <p className="tutorial-tooltip-text">{step.content}</p>
      </div>

      {/* Progress indicator */}
      <div className="tutorial-progress">
        <span className="tutorial-progress-text">
          Step {stepIndex + 1} of {totalSteps}
        </span>
        <div className="tutorial-progress-dots">
          {Array.from({ length: totalSteps }, (_, i) => (
            <span
              key={i}
              className={`tutorial-progress-dot ${i === stepIndex ? 'active' : ''} ${i < stepIndex ? 'completed' : ''}`}
            />
          ))}
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="tutorial-nav">
        <button
          className="tutorial-btn tutorial-btn--skip"
          onClick={onSkip}
          type="button"
        >
          Exit
        </button>

        <div className="tutorial-nav-main">
          {!isFirstStep && (
            <button
              className="tutorial-btn tutorial-btn--back"
              onClick={onPrev}
              type="button"
            >
              Back
            </button>
          )}

          {/* Hide Next button when user must click the highlighted element */}
          {!step.requiresClick && (
            <button
              className="tutorial-btn tutorial-btn--next"
              onClick={onNext}
              type="button"
            >
              {isLastStep ? 'Done' : 'Next'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TutorialTooltip;
